import type { ZodTypeAny } from "zod";

export class ApiError extends Error {
  constructor(
    public status: number,
    public bodyJson: unknown,
    message?: string,
  ) {
    super(message ?? `HTTP ${status}`);
  }
}

/**
 * Best-effort parse of a non-2xx response body as JSON. Returns `null` when
 * the body isn't JSON (e.g. a plain-text 502 from upstream) — the status
 * code is the signal that matters in that case. Shared between apiFetch and
 * any request layer that can't route through it (e.g. file-upload.ts's
 * multipart uploader).
 */
export async function parseErrorBody(res: Response): Promise<unknown> {
  try {
    return await res.json();
  } catch {
    return null;
  }
}

type RequestOpts<TBody> = {
  method?: "GET" | "POST" | "PATCH" | "DELETE";
  path: string; // e.g. "/api/v1/users/me"
  body?: TBody;
  headers?: Record<string, string>;
  signal?: AbortSignal;
};

/**
 * Typed fetch helper:
 *   - sends/receives JSON
 *   - validates response with the provided Zod schema (or void if none)
 *   - throws ApiError on non-2xx, with the parsed body for error UI
 *   - always uses credentials:include so session cookies flow
 */
export async function apiFetch<TBody, TResponse>(
  opts: RequestOpts<TBody>,
  responseSchema: ZodTypeAny | null,
): Promise<TResponse> {
  const headers: Record<string, string> = { ...opts.headers };
  if (opts.body !== undefined && !headers["Content-Type"]) {
    headers["Content-Type"] = "application/json";
  }

  const init: RequestInit = {
    method: opts.method ?? "GET",
    headers,
    credentials: "include",
    signal: opts.signal,
  };
  if (opts.body !== undefined) {
    init.body = JSON.stringify(opts.body);
  }

  const res = await fetch(opts.path, init);

  if (!res.ok) {
    throw new ApiError(res.status, await parseErrorBody(res));
  }

  // Empty-body cases: 204 No Content (DELETE/logout) and 202 Accepted (magic-link).
  // Also defensively check Content-Length for any other empty-body responses.
  const contentLength = res.headers.get("content-length");
  if (res.status === 204 || res.status === 202 || contentLength === "0") {
    return undefined as TResponse;
  }

  const json = await res.json();
  if (responseSchema === null) return json as TResponse;
  try {
    return responseSchema.parse(json) as TResponse;
  } catch {
    // Schema mismatch is a contract bug, not transient — surface as ApiError so retry predicate skips it.
    throw new ApiError(res.status, json, "response failed schema validation");
  }
}
